/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { CaseEventDTO } from 'api/model';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { useQueryClient } from '@tanstack/react-query';
import { useCaseMarkEventsAsReadByAdmin } from 'api/case/case';
import {
  getTransportLogisticsGetUnhandledAssignmentRescheduledToAfterMidnightQueryKey,
  useTransportLogisticsGetUnhandledAssignmentRescheduledToAfterMidnight,
} from 'api/transport-logistics/transport-logistics';
import { formatDateTime } from 'utils/date-helpers';
import CaseOpenLink from 'components/CaseOpenLink';
import Checkbox from 'components/inputs/Checkbox';
import useUsers from 'contexts/basicData/useUsers';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import useBaseCaseEventWidgetColumnSettings from './useBaseCaseEventWidgetColumnSettings';
import {
  Centered,
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';

const TABLE_NAME = 'assignment-rescheduled-to-after-midnight';

const AssignmentRescheduledToAfterMidnightWidget = () => {
  const [events, setEvents] = useState<CaseEventDTO[]>([]);
  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const users = useUsers();

  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useCaseMarkEventsAsReadByAdmin({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            `${getTransportLogisticsGetUnhandledAssignmentRescheduledToAfterMidnightQueryKey()}`,
          ]);
        },
      },
    });

  const getEvents =
    useTransportLogisticsGetUnhandledAssignmentRescheduledToAfterMidnight(
      {
        startDate: daterange[0] ?? undefined,
        endDate: daterange[1] ?? undefined,
        businessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getEvents.data) {
      setEvents(getEvents.data);
    } else {
      setEvents([] as CaseEventDTO[]);
    }
  }, [getEvents.data]);

  const filteredEditedBookedTos = useMemo(() => {
    if (!searchText.length) {
      return events;
    }
    return events
      ? events.filter((edits) => {
          return (
            edits.registrationNumber
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (users[edits.createdByID]?.name ?? '')
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        })
      : [];
  }, [events, searchText, users]);

  const baseColumnSettings = useBaseCaseEventWidgetColumnSettings();
  const columnSettings = useMemo(() => {
    const columns: ColumnSetting<CaseEventDTO, any>[] = [...baseColumnSettings];

    columns.push(
      {
        head: ' ',
        render: (row) => (
          <Centered>
            <CaseOpenLink caseID={row.caseID} />
          </Centered>
        ),
        width: 20,
      },
      {
        head: 'Klart',
        render: (row) => (
          <Centered>
            <Checkbox
              disabled={markAsReadStatus === 'loading'}
              onChange={async () => {
                setOngoingSaves([...ongoingSaves, row.id]);
                await markEventAsRead({ data: [row.id] });
                setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
              }}
            />
          </Centered>
        ),
        width: 30,
      }
    );
    return columns;
  }, [baseColumnSettings, markAsReadStatus, markEventAsRead, ongoingSaves]);

  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.AssignmentRescheduledToAfterMidnight]}
      count={events.length}
      isLoading={getEvents.isLoading}
      info="Markera som klar för att markera som läst."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredEditedBookedTos}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
          useColumnWidthAsFlexAmount
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default AssignmentRescheduledToAfterMidnightWidget;
